import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

const INPUT_FMT = 'YYYY-MM-DD HH:mm';

export type QuickstatBookingEmailSource = {
  pickup_person?: string | null;
  pickup_phone?: string | null;
  pickup_name?: string | null;
  pickup_address?: string | null;
  pickup_address2?: string | null;
  pickup_address3?: string | null;
  pickup_city?: string | null;
  pickup_state?: string | null;
  pickup_zip?: string | null;
  pickup_country?: string | null;
  delivery_person?: string | null;
  delivery_phone?: string | null;
  delivery_name?: string | null;
  delivery_address?: string | null;
  delivery_address2?: string | null;
  delivery_address3?: string | null;
  delivery_city?: string | null;
  delivery_state?: string | null;
  delivery_zip?: string | null;
  delivery_country?: string | null;
  shipment_ready_input_text?: string | null;
  desired_delivery_input_text?: string | null;
  desired_delivery_type?: string | null;
  package_packout_input_text?: string | null;
};

/** Title-case a city-style phrase (e.g. `chicago` → `Chicago`, `SAN DIEGO` → `San Diego`). */
export function formatQuickstatDeliveryCityDisplay(
  city: string | null | undefined
): string {
  const s = (city ?? '').trim();
  if (!s) return '';
  return s
    .split(/\s+/)
    .map((word) =>
      word
        .split('-')
        .map((seg) =>
          seg.length === 0
            ? seg
            : seg.charAt(0).toUpperCase() + seg.slice(1).toLowerCase()
        )
        .join('-')
    )
    .join(' ');
}

/**
 * State for email copy: 2-letter codes stay uppercase (`il` → `IL`); longer names are title-cased.
 */
export function formatQuickstatDeliveryStateDisplay(
  state: string | null | undefined
): string {
  const s = (state ?? '').trim();
  if (!s) return '';
  if (/^[A-Za-z]{2}$/.test(s)) {
    return s.toUpperCase();
  }
  return formatQuickstatDeliveryCityDisplay(s);
}

/**
 * For "City, …" email lines: prefer delivery state; if absent, use delivery country
 * (same formatting as {@link formatQuickstatDeliveryStateDisplay}).
 */
export function formatQuickstatDeliveryStateOrCountryDisplay(
  booking: Pick<
    QuickstatBookingEmailSource,
    'delivery_state' | 'delivery_country'
  >
): string {
  const fromState = formatQuickstatDeliveryStateDisplay(booking.delivery_state);
  if (fromState) return fromState;
  return formatQuickstatDeliveryStateDisplay(booking.delivery_country);
}

export function formatQuickstatEmailDate(
  raw: string | null | undefined,
  fallbackWhenEmpty = ''
): string {
  const s = (raw ?? '').trim();
  if (!s) return fallbackWhenEmpty;
  const d = dayjs(s, INPUT_FMT, true);
  if (!d.isValid()) return s;
  return d.format('MMM-D-YYYY HH:mm');
}

function cityStateZip(
  city?: string | null,
  state?: string | null,
  zip?: string | null
): string {
  const c = (city ?? '').trim();
  const st = (state ?? '').trim();
  const z = (zip ?? '').trim();
  if (!c && !st && !z) return '';
  if (c && st && z) return `${c}  ${st} ${z}`;
  return [c, st, z].filter(Boolean).join(' ');
}

/** Safe for HTML text nodes and attributes (booking fields may contain `<`, `&`, etc.). */
export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Plain text with newlines → escaped HTML with `<br />` (e.g. leg-2 placeholder). */
export function plainTextToHtmlBreaks(text: string): string {
  return escapeHtml(text).replace(/\n/g, '<br />\n');
}

/** Same value as the pickup/delivery table "Deadline Date" (subject line + table). */
export function formatDeliveryDeadlineDisplay(
  b: QuickstatBookingEmailSource
): string {
  const isAsap = (b.desired_delivery_type || '').toLowerCase() === 'asap';
  const deadlineRaw = (b.desired_delivery_input_text ?? '').trim();
  if (deadlineRaw) {
    return formatQuickstatEmailDate(deadlineRaw);
  }
  return isAsap ? 'ASAP' : '[Deadline]';
}

export function formatLn2DropOffDateDisplay(
  b: QuickstatBookingEmailSource
): string {
  return (
    formatQuickstatEmailDate(b.package_packout_input_text) || '[Packout date]'
  );
}

export function formatLn2DropOffDateLine(
  b: QuickstatBookingEmailSource
): string {
  return `LN2 drop off date: ${formatLn2DropOffDateDisplay(b)}`;
}

function pickupDeliveryDatesAndLines(b: QuickstatBookingEmailSource): {
  ready: string;
  deadline: string;
  pickupLines: string[];
  deliveryLines: string[];
} {
  const ready =
    formatQuickstatEmailDate(b.shipment_ready_input_text) || '[Ready date]';
  const deadline = formatDeliveryDeadlineDisplay(b);

  const pickupLines = [
    b.pickup_person,
    b.pickup_phone,
    b.pickup_name,
    b.pickup_address,
    b.pickup_address2,
    b.pickup_address3,
    cityStateZip(b.pickup_city, b.pickup_state, b.pickup_zip),
    b.pickup_country,
  ]
    .map((l) => (l ?? '').trim())
    .filter(Boolean);

  const deliveryLines = [
    b.delivery_person,
    b.delivery_phone,
    b.delivery_name,
    b.delivery_address,
    b.delivery_address2,
    b.delivery_address3,
    cityStateZip(b.delivery_city, b.delivery_state, b.delivery_zip),
    b.delivery_country,
  ]
    .map((l) => (l ?? '').trim())
    .filter(Boolean);

  return { ready, deadline, pickupLines, deliveryLines };
}

/**
 * Two-column Pickup / Delivery table aligned with QuickOnline confirmation HTML
 * (`bgcolor="#99ccff"` headers, dates + `<hr />`, address blocks).
 */
export function formatPickupDeliveryTableHtml(
  b: QuickstatBookingEmailSource
): string {
  const { ready, deadline, pickupLines, deliveryLines } =
    pickupDeliveryDatesAndLines(b);
  const pickupCell =
    pickupLines.length > 0
      ? `<b>Ready Date: ${escapeHtml(ready)}</b><br />\n<hr size="1" />\n<br />\n${pickupLines.map((l) => `${escapeHtml(l)}<br />`).join('\n')}`
      : `<b>Ready Date: ${escapeHtml(ready)}</b><br />\n<hr size="1" />\n`;

  const deliveryCell =
    deliveryLines.length > 0
      ? `<b>Deadline Date: ${escapeHtml(deadline)}</b><br />\n<hr size="1" />\n<br />\n${deliveryLines.map((l) => `${escapeHtml(l)}<br />`).join('\n')}`
      : `<b>Deadline Date: ${escapeHtml(deadline)}</b><br />\n<hr size="1" />\n`;

  return `<table cellpadding="8" cellspacing="0" border="1" width="100%" style="border-collapse:collapse;margin:10px 0 14px;font-family:Arial,Helvetica,sans-serif;font-size:12px;color:#000;">
<tbody>
<tr>
<td colspan="2" bgcolor="#99CCFF" align="center"><b>Pickup</b></td>
<td colspan="2" bgcolor="#99CCFF" align="center"><b>Delivery</b></td>
</tr>
<tr>
<td colspan="2" valign="top">${pickupCell}</td>
<td colspan="2" valign="top">${deliveryCell}</td>
</tr>
</tbody>
</table>`;
}

/** Second leg pickup through delivery — fill in manually (multi-leg LN2 templates). */
export const LEG2_EMAIL_PLACEHOLDER = '[Second Leg Pickup Delivery]';

export function formatPickupDeliverySection(
  b: QuickstatBookingEmailSource
): string {
  const { ready, deadline, pickupLines, deliveryLines } =
    pickupDeliveryDatesAndLines(b);

  return [
    'Pickup',
    `Ready Date: ${ready}`,
    '',
    ...pickupLines,
    '',
    'Delivery',
    `Deadline Date: ${deadline}`,
    '',
    ...deliveryLines,
  ].join('\n');
}

export function bookingRowToEmailSource(
  row: Record<string, unknown> | null
): QuickstatBookingEmailSource | null {
  if (!row) return null;
  const s = (k: string): string | null => {
    const v = row[k];
    if (v == null) return null;
    return String(v);
  };
  return {
    pickup_person: s('pickup_person'),
    pickup_phone: s('pickup_phone'),
    pickup_name: s('pickup_name'),
    pickup_address: s('pickup_address'),
    pickup_address2: s('pickup_address2'),
    pickup_address3: s('pickup_address3'),
    pickup_city: s('pickup_city'),
    pickup_state: s('pickup_state'),
    pickup_zip: s('pickup_zip'),
    pickup_country: s('pickup_country'),
    delivery_person: s('delivery_person'),
    delivery_phone: s('delivery_phone'),
    delivery_name: s('delivery_name'),
    delivery_address: s('delivery_address'),
    delivery_address2: s('delivery_address2'),
    delivery_address3: s('delivery_address3'),
    delivery_city: s('delivery_city'),
    delivery_state: s('delivery_state'),
    delivery_zip: s('delivery_zip'),
    delivery_country: s('delivery_country'),
    shipment_ready_input_text: s('shipment_ready_input_text'),
    desired_delivery_input_text: s('desired_delivery_input_text'),
    desired_delivery_type: s('desired_delivery_type'),
    package_packout_input_text: s('package_packout_input_text'),
  };
}
